/**
 * Single shared Socket.io client for the agent backend. Lazily connects
 * on first `getEmbedSocket()`. The base URL is configured by the embed
 * surface via `configureApiBase` so partner / cross-origin embeds can
 * point at agent.ordo.com while same-origin (Ordo apps on `*.ordo.com`)
 * uses relative paths.
 *
 * `onSocketConnectedChange` lets surfaces show a "Reconnecting…" banner
 * when the WebSocket itself is down — independent of `turnState`. Per
 * `apps/agent/plans/plan-turn-progress-feedback.md`.
 *
 * Bearer auth: when `configureAgentToken(getter)` is set, the embed
 * skips cookie auth and instead carries `Authorization: Bearer <token>`
 * (HTTP) or `auth: { token }` (Socket.IO handshake). Required for any
 * embed where the host origin is cross-site relative to the agent
 * (localhost / ngrok dev; cross-domain partner embeds in production).
 * Token getters are awaited at connect time and on every HTTP request,
 * so token rotation is the host's prerogative.
 */

import { sameEnvOrigin } from '@ordo/ui/same-env-origin'
import { io, type Socket } from 'socket.io-client'

let socket: Socket | null = null
let configuredBaseUrl = ''
let tokenGetter: (() => Promise<string | null>) | null = null
const connectedListeners = new Set<(connected: boolean) => void>()

export function configureApiBase(baseUrl: string): void {
  const next = resolveApiBase(baseUrl)
  if (next === configuredBaseUrl) {
    return
  }
  configuredBaseUrl = next
  if (socket) {
    socket.disconnect()
    socket = null
  }
}

export function getApiBase(): string {
  return configuredBaseUrl
}

/**
 * Normalize a host-supplied `apiBaseUrl`. Every host app passes
 * `VITE_AGENT_API_URL ?? 'https://agent.ordo.com'` — that production
 * fallback is baked into the client bundle, and preview envs build their
 * bundles in-env, so a page served from `<app>.<envId>.preview.ordo.com`
 * would call PRODUCTION agent (401 noise, and a real cross-env leak).
 * `sameEnvOrigin` retargets it onto the page's own parent domain; prod,
 * localhost dev, and cross-domain partner embeds are all untouched.
 */
export function resolveApiBase(baseUrl: string): string {
  return sameEnvOrigin(baseUrl).replace(/\/$/, '')
}

/** Register an async token getter for cross-origin auth. Pass `null`
 *  to remove. When set, the embed carries the token via Authorization
 *  header (HTTP) and Socket.IO handshake auth instead of cookies. */
export function configureAgentToken(getter: (() => Promise<string | null>) | null): void {
  if (tokenGetter === getter) {
    return
  }
  tokenGetter = getter
  // Disconnect so the next call rebuilds the socket with the new
  // handshake auth. Cheap; the next getEmbedSocket() reconnects.
  if (socket) {
    socket.disconnect()
    socket = null
  }
}

/** Resolve the current token, if any. Used by api-client and the
 *  socket handshake. Always async so the host can fetch on demand. */
export async function getAgentToken(): Promise<string | null> {
  if (!tokenGetter) {
    return null
  }
  try {
    return (await tokenGetter()) ?? null
  } catch {
    return null
  }
}

export function getEmbedSocket(): Socket {
  if (!socket) {
    // Token is fetched at connect-time via the auth callback; Socket.IO
    // re-invokes this function on every reconnect, so a fresh token is
    // pulled after a network drop / browser tab wake-up.
    const authCb = (cb: (data: { token?: string }) => void) => {
      void getAgentToken().then((token) => {
        cb(token ? { token } : {})
      })
    }
    socket = io(configuredBaseUrl || undefined, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      withCredentials: true,
      auth: authCb,
    })
    socket.on('connect', () => {
      for (const cb of connectedListeners) {
        cb(true)
      }
    })
    socket.on('disconnect', () => {
      for (const cb of connectedListeners) {
        cb(false)
      }
    })
    // Server refused our conv-room join because the socket was anonymous
    // — force a reconnect so the auth callback re-runs with a fresh
    // token. Without this the embed silently hangs on every event the
    // server emits to the conv room. Guard against infinite loops with
    // a single-shot flag per socket lifetime.
    let rejoinAttempted = false
    socket.on('host:join-failed', (e: { conversationId?: string; reason?: string }) => {
      console.warn('[agent-embed] host:join-failed', e)
      if (e.reason === 'unauthenticated' && !rejoinAttempted) {
        rejoinAttempted = true
        socket?.disconnect()
        socket?.connect()
      }
    })
  }
  return socket
}

export function onSocketConnectedChange(cb: (connected: boolean) => void): () => void {
  cb(socket?.connected ?? false)
  connectedListeners.add(cb)
  return () => {
    connectedListeners.delete(cb)
  }
}
